import React from "react";
import { fmtINR } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";

// Print sheet for the interim cash scroll — mirrors InvoicePrintSheet: a sheet
// hidden on screen and revealed only under @media print, so window.print() (→
// "Save as PDF") produces a clean A4 document.

export const SCROLL_PRINT_STYLES = `
  @media print {
    @page { size: A4 portrait; margin: 14mm 12mm; }

    html, body {
      width: 210mm;
      background: ${IPD_COLORS.white} !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    aside, nav, .ipd-scroll-no-print { display: none !important; }
    /* App chrome only — never the sheet's own <header>, which carries the
       facility branding. */
    header:not(.ipd-scroll-header) { display: none !important; }

    body * { visibility: hidden; }
    .ipd-scroll-print-sheet, .ipd-scroll-print-sheet * { visibility: visible; }

    .ipd-scroll-print-sheet {
      display: block !important;
      position: fixed;
      left: 0; top: 0;
      width: 100%;
      padding: 0; margin: 0;
      background: ${IPD_COLORS.white};
      z-index: 99999;
    }
  }

  @media screen { .ipd-scroll-print-sheet { display: none !important; } }

  .ipd-scroll-print-sheet {
    font-family: "Segoe UI", Arial, sans-serif;
    color: ${IPD_COLORS.primary};
    font-size: 11pt;
    line-height: 1.45;
  }
  .ipd-scroll-page { max-width: 186mm; margin: 0 auto; }
  .ipd-scroll-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    border-bottom: 2px solid ${IPD_COLORS.primary}; padding-bottom: 10px; margin-bottom: 14px;
  }
  .ipd-scroll-brand { display: flex; gap: 12px; align-items: flex-start; }
  .ipd-scroll-logo { max-width: 40mm; max-height: 16mm; object-fit: contain; }
  .ipd-scroll-facility { font-size: 16pt; font-weight: 700; margin: 0 0 4px; }
  .ipd-scroll-meta { font-size: 9pt; color: ${IPD_COLORS.textSecondary}; margin: 0; }
  .ipd-scroll-title-block { text-align: right; }
  .ipd-scroll-doc-label { font-size: 8pt; font-weight: 700; letter-spacing: 0.08em; color: ${IPD_COLORS.textSecondary}; margin: 0 0 4px; text-transform: uppercase; }
  .ipd-scroll-doc-no { font-size: 14pt; font-weight: 800; margin: 0 0 4px; }
  .ipd-scroll-status { display: inline-block; margin-top: 4px; padding: 2px 10px; border: 1px solid ${IPD_COLORS.border}; border-radius: 12px; font-size: 8.5pt; font-weight: 600; }

  .ipd-scroll-info {
    display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;
    margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid ${IPD_COLORS.border};
  }
  .ipd-scroll-info-label { font-size: 7.5pt; font-weight: 700; letter-spacing: 0.06em; color: ${IPD_COLORS.textSecondary}; text-transform: uppercase; margin: 0 0 4px; }
  .ipd-scroll-info-value { font-size: 10pt; font-weight: 600; margin: 0; }

  .ipd-scroll-section-title { font-size: 8pt; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: ${IPD_COLORS.textSecondary}; margin: 0 0 8px; }

  .ipd-scroll-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 9.5pt; }
  .ipd-scroll-table th {
    text-align: left; font-size: 7.5pt; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
    color: ${IPD_COLORS.textSecondary}; border-bottom: 1.5px solid ${IPD_COLORS.primary}; padding: 6px 4px;
  }
  .ipd-scroll-table th.num, .ipd-scroll-table td.num { text-align: right; }
  .ipd-scroll-table td { padding: 7px 4px; border-bottom: 1px solid ${IPD_COLORS.border}; vertical-align: top; }
  .ipd-scroll-table tfoot td { font-weight: 800; border-top: 1.5px solid ${IPD_COLORS.primary}; border-bottom: none; }

  .ipd-scroll-summary { width: 78mm; margin-left: auto; font-size: 10pt; margin-bottom: 16px; }
  .ipd-scroll-summary-row { display: flex; justify-content: space-between; padding: 3px 0; }
  .ipd-scroll-summary-row.net { margin-top: 8px; padding: 8px 10px; border: 1.5px solid ${IPD_COLORS.primary}; font-weight: 800; font-size: 11pt; }

  .ipd-scroll-footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid ${IPD_COLORS.border}; font-size: 8pt; color: ${IPD_COLORS.textMuted}; text-align: center; }
  .ipd-scroll-footer-statement { font-size: 9pt; font-weight: 600; color: ${IPD_COLORS.textSecondary}; margin: 0 0 4px; }
  .ipd-scroll-footer-line { margin: 0; }
  .ipd-scroll-remarks { font-size: 9.5pt; margin: 0 0 16px; }
`;

const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
};

// `hospital` mirrors InvoicePrintSheet's branded header: { name, address, logo,
// brandStatement } — the facility identity the printed scroll is issued under.
// `kind` is INTERIM or FINAL; a FINAL scroll also prints the cash reconciliation
// ({ systemCash, declaredCash, variance, remarks }) the cashier closed the day on.
export default function ScrollPrintSheet({
  scroll,
  session,
  counterCode,
  hospital,
  kind = "INTERIM",
  reconciliation,
}) {
  if (!scroll) return null;

  const facility = hospital || {};
  const isFinal = kind === "FINAL";
  const docLabel = isFinal ? "Final Cash Scroll" : "Interim Cash Scroll";

  const counter = scroll.counterCode || session?.counterCode || counterCode || "—";
  const sessionId = scroll.sessionId || session?.id || "—";
  const openedBy = scroll.openedBy || session?.openedBy || "—";
  const openedAt = scroll.openedAt || session?.openedAt || null;
  const status = scroll.status || session?.status || "OPEN";
  const txns = scroll.transactions || [];
  const modeTotal = scroll.modes.reduce((s, m) => s + Number(m.amount || 0), 0);

  return (
    <div className="ipd-scroll-print-sheet">
      <div className="ipd-scroll-page">
        <header className="ipd-scroll-header">
          <div className="ipd-scroll-brand">
            {facility.logo && (
              <img className="ipd-scroll-logo" src={facility.logo} alt="" />
            )}
            <div>
              <p className="ipd-scroll-facility">{facility.name || "Cash Counter"}</p>
              {facility.address && <p className="ipd-scroll-meta">{facility.address}</p>}
              <p className="ipd-scroll-meta">Counter {counter}</p>
            </div>
          </div>
          <div className="ipd-scroll-title-block">
            <p className="ipd-scroll-doc-label">{docLabel}</p>
            <p className="ipd-scroll-doc-no">{counter}</p>
            <p className="ipd-scroll-meta">Session {String(sessionId).slice(0, 8)}</p>
            <span className="ipd-scroll-status">{status}</span>
          </div>
        </header>

        <section className="ipd-scroll-info">
          <div>
            <p className="ipd-scroll-info-label">Counter</p>
            <p className="ipd-scroll-info-value">{counter}</p>
          </div>
          <div>
            <p className="ipd-scroll-info-label">Opened by</p>
            <p className="ipd-scroll-info-value">{openedBy}</p>
          </div>
          <div>
            <p className="ipd-scroll-info-label">Opened at</p>
            <p className="ipd-scroll-info-value">{fmtDT(openedAt)}</p>
          </div>
        </section>

        <p className="ipd-scroll-section-title">Totals by payment mode</p>
        <table className="ipd-scroll-table">
          <thead>
            <tr>
              <th style={{ width: "50%" }}>Mode</th>
              <th className="num" style={{ width: "20%" }}>Txns</th>
              <th className="num" style={{ width: "30%" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {scroll.modes.map((m) => (
              <tr key={m.key}>
                <td>{m.label}</td>
                <td className="num">{m.count || 0}</td>
                <td className="num">{fmtINR(m.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="num">{scroll.receiptCount || 0}</td>
              <td className="num">{fmtINR(modeTotal)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="ipd-scroll-summary">
          <div className="ipd-scroll-summary-row">
            <span>Receipts ({scroll.receiptCount || 0})</span>
            <span>{fmtINR(scroll.receiptAmount)}</span>
          </div>
          <div className="ipd-scroll-summary-row">
            <span>Refunds ({scroll.refundCount || 0})</span>
            <span>− {fmtINR(scroll.refundAmount)}</span>
          </div>
          {scroll.depositAmount > 0 && (
            <div className="ipd-scroll-summary-row">
              <span>Deposits ({scroll.depositCount || 0})</span>
              <span>{fmtINR(scroll.depositAmount)}</span>
            </div>
          )}
          <div className="ipd-scroll-summary-row net">
            <span>Net collection</span>
            <span>{fmtINR(scroll.netCollection)}</span>
          </div>
        </div>

        {reconciliation && (
          <>
            <p className="ipd-scroll-section-title">Cash reconciliation</p>
            <div className="ipd-scroll-summary">
              <div className="ipd-scroll-summary-row">
                <span>Expected cash (system)</span>
                <span>{fmtINR(reconciliation.systemCash)}</span>
              </div>
              <div className="ipd-scroll-summary-row">
                <span>Declared cash</span>
                <span>{fmtINR(reconciliation.declaredCash)}</span>
              </div>
              <div className="ipd-scroll-summary-row net">
                <span>Variance</span>
                <span>{fmtINR(reconciliation.variance)}</span>
              </div>
            </div>
            {reconciliation.remarks && (
              <p className="ipd-scroll-remarks">Remarks: {reconciliation.remarks}</p>
            )}
          </>
        )}

        {txns.length > 0 && (
          <>
            <p className="ipd-scroll-section-title">Transactions ({txns.length})</p>
            <table className="ipd-scroll-table">
              <thead>
                <tr>
                  <th style={{ width: "6%" }}>#</th>
                  <th style={{ width: "26%" }}>Receipt</th>
                  <th style={{ width: "18%" }}>Type</th>
                  <th style={{ width: "16%" }}>Mode</th>
                  <th style={{ width: "18%" }}>Time</th>
                  <th className="num" style={{ width: "16%" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t, idx) => (
                  <tr key={t.id || idx}>
                    <td>{idx + 1}</td>
                    <td>{t.receipt}</td>
                    <td>{t.type}</td>
                    <td>{t.mode}</td>
                    <td>{fmtDT(t.time)}</td>
                    <td className="num">{fmtINR(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <footer className="ipd-scroll-footer">
          {facility.brandStatement && (
            <p className="ipd-scroll-footer-statement">{facility.brandStatement}</p>
          )}
          {facility.address && <p className="ipd-scroll-footer-line">{facility.address}</p>}
          <p className="ipd-scroll-footer-line">
            Computer-generated {isFinal ? "final" : "interim"} scroll · Counter {counter} · Printed{" "}
            {new Date().toLocaleString("en-IN")}
          </p>
        </footer>
      </div>
    </div>
  );
}

export function triggerScrollPrint() {
  window.print();
}
